import * as Sentry from '@sentry/react';
import { AxiosError } from 'axios';
import { ComponentProps, useEffect } from 'react';
import { Await, useAsyncError } from 'react-router-dom';
import useError from '../hooks/error';
import { CLIENT_SIDE_NETWORK_ERROR_CODES } from '../hooks/http-request';

export type ErrorHandlingAwaitProps = ComponentProps<typeof Await>;

export function ErrorHandlingAwait({
  errorElement,
  ...props
}: ErrorHandlingAwaitProps) {
  return (
    <Await {...props} errorElement={errorElement ?? <DefaultErrorElement />} />
  );
}

const DefaultErrorElement = () => {
  const { setError } = useError();
  const asyncError = useAsyncError() as AxiosError;

  useEffect(() => {
    // Client side network errors are not surfaced to Sentry
    const errorCode = asyncError?.code;
    if (errorCode && CLIENT_SIDE_NETWORK_ERROR_CODES.includes(errorCode)) {
      return;
    }

    if (!asyncError.response?.status || asyncError.response?.status === 500) {
      Sentry.captureException(asyncError);
    }
  }, [asyncError]);

  const errorMessage =
    (asyncError.response?.data as { message: string })?.message ||
    asyncError.message;

  useEffect(() => {
    setError({
      status: asyncError.response?.status || 500,
      message: errorMessage,
      data: asyncError.response?.data || (asyncError as Error)?.stack,
    });
  }, [asyncError, errorMessage, setError]);

  return null;
};

export default ErrorHandlingAwait;
